(function () {
  'use strict';

  /* Country selector page.
     The cookie contract below is shared with assets/geolocation-banner.js - a manual choice made
     here is what that script reads to route returning visitors and to stop prompting them. The two
     files are deliberately standalone (no globals, no build step), so the constants and the three
     helpers are duplicated; src/__tests__/geo-banner-verify.mjs asserts they never drift apart. */

  /* Only the choice cookie is written here, never hilo_geo_banner_dismissed. The choice outranks a
     prior dismissal and records the deliberate market selection used for cross-store routing. */
  var COOKIE_CHOICE = 'hilo_geo_choice';
  var COOKIE_DAYS = 400;

  function cookieDomain() {
    var host = (window.location.hostname || '').toLowerCase();
    if (host === 'hilo.com' || /\.hilo\.com$/.test(host)) return '.hilo.com';
    return null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    var domain = cookieDomain();
    document.cookie =
      name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax' + (domain ? '; domain=' + domain : '');
  }

  function parseList(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(function (item) {
        return item.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function parseSubfolders(value) {
    var map = {};
    if (!value) return map;
    var lines = value.split(/[\r\n]+/);
    for (var i = 0; i < lines.length; i++) {
      var parts = lines[i].split('|');
      if (parts.length < 2) continue;
      var subfolder = parts[0].trim().toLowerCase();
      if (!subfolder) continue;
      var codes = parseList(parts[1]);
      for (var j = 0; j < codes.length; j++) {
        map[codes[j]] = subfolder;
      }
    }
    return map;
  }

  function withLocalePrefix(urlStr, locale, subfolder) {
    if (!subfolder) return urlStr;
    var segment = (locale || 'en') + '-' + subfolder;
    try {
      var url = new URL(urlStr);
      var path = url.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
      url.pathname = '/' + segment + (path ? '/' + path : '');
      return url.toString();
    } catch (error) {
      return urlStr;
    }
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  class CountrySelector extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      try {
        this.init();
      } catch (error) {
        /* fail closed: the list stays rendered as plain, inert text */
      }
    }

    init() {
      this.currentLocale = (this.getAttribute('data-current-locale') || 'en').toLowerCase();
      this.subfolders = parseSubfolders(this.getAttribute('data-market-subfolders'));
      this.returnTo = this.getAttribute('data-return-to') || '/';

      // Country names in the page language: the merchant types "Germany | DE" once and the German
      // page still reads "Deutschland". Same source as the geolocation popup (Intl.DisplayNames); the
      // typed name stays when the browser lacks the API or the merchant switched this off.
      if (this.getAttribute('data-translate-names') !== 'false') this.translateCountryNames();

      this.form = this.querySelector('.shopify-localization-form');
      this.countryInput = this.querySelector('[data-cs-country-input]');
      this.localeInput = this.querySelector('[data-cs-locale-input]');
      // Shopify injects a return_to field into localization forms. Keep every generated/custom copy
      // consistent so request parsing order cannot send the visitor back to the selector page.
      this.returnInputs = Array.prototype.slice.call(this.form ? this.form.querySelectorAll('[name="return_to"]') : []);

      var self = this;
      Array.prototype.slice.call(this.querySelectorAll('.country-selector__country')).forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.onSelect(btn);
        });
      });
    }

    translateCountryNames() {
      if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return;
      var names;
      try {
        names = new Intl.DisplayNames([this.currentLocale], { type: 'region', fallback: 'none' });
      } catch (error) {
        return;
      }
      Array.prototype.slice.call(this.querySelectorAll('.country-selector__country')).forEach(function (btn) {
        var code = (btn.getAttribute('data-country-code') || '').trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(code)) return;
        var name = null;
        try {
          name = names.of(code);
        } catch (error) {
          name = null;
        }
        if (name) btn.textContent = name;
      });
    }

    // Keep the visitor's current language when the target market publishes it, otherwise use that
    // market's first published language. Requesting an unpublished locale/market pair 404s.
    localeFor(button) {
      var locales = parseList(button.getAttribute('data-locales'));
      if (!locales.length) return 'en';
      if (locales.indexOf(this.currentLocale) !== -1) return this.currentLocale;
      return locales[0];
    }

    onSelect(button) {
      if (isDesignMode()) return;

      var code = (button.getAttribute('data-country-code') || '').toUpperCase();
      if (!code) return;
      var external = (button.getAttribute('data-external-url') || '').trim();
      var locale = this.localeFor(button);
      var subfolder = this.subfolders[code.toLowerCase()] || '';

      if (external) {
        var dest = withLocalePrefix(external, locale, subfolder);
        setCookie(COOKIE_CHOICE, 'geo|' + dest + '|' + code + '|' + locale, COOKIE_DAYS);
        window.location.href = dest;
        return;
      }

      // Record this store, market and language as the deliberate choice. The sibling store reads the
      // same cookie and routes returning visitors back here instead of prompting them again.
      setCookie(
        COOKIE_CHOICE,
        'geo|' + withLocalePrefix(window.location.origin, locale, subfolder) + '|' + code + '|' + locale,
        COOKIE_DAYS
      );

      if (this.form && this.countryInput && this.localeInput) {
        this.countryInput.disabled = false;
        this.countryInput.value = code;
        this.localeInput.disabled = false;
        this.localeInput.value = locale;
        var returnTo = this.returnTo;
        this.returnInputs.forEach(function (input) {
          input.value = returnTo;
        });
        this.form.submit();
        return;
      }

      window.location.href = this.returnTo;
    }
  }

  if ('customElements' in window && !window.customElements.get('hilo-country-selector')) {
    window.customElements.define('hilo-country-selector', CountrySelector);
  }
})();
